import { PostHog } from 'posthog-js'
import { RxCollection, RxDatabase, RxError, RxState } from 'rxdb'
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  share,
} from 'rxjs'
import { filter } from 'rxjs/operators'

import { createAppUserReplication } from '@/system/rxdb/replications/appUser.ts'
import { createAssessmentReplication } from '@/system/rxdb/replications/assessment.ts'
import { createAssessmentAdditionalInfoReplication } from '@/system/rxdb/replications/assessmentAdditionalInfo'
import { createAssessmentDetailReplication } from '@/system/rxdb/replications/assessmentDetail.ts'
import { createAssessmentMeasurementReplication } from '@/system/rxdb/replications/assessmentMeasurement.ts'
import { createSignatureReplication } from '@/system/rxdb/replications/assessmentSignature.ts'
import { createAssignmentReplication } from '@/system/rxdb/replications/assignment.ts'
import { AppUser } from '@/system/rxdb/schemas/appUser.ts'

export type AuthState = 'PENDING' | 'IS_REQUIRED' | 'IS_NOT_REQUIRED'

export type ApplicationAuthState = {
  requireAuthentication?: AuthState
  shouldHandleLogout?: boolean
}

export enum NetworkStatus {
  SYNCING = 'syncing',
  IDLE = 'idle',
}

export type ApplicationNetworkState = {
  status: NetworkStatus
  lastError: { error: Error | null }
}

const tryCatch = async <T>(fn: () => Promise<T>) => {
  try {
    return await fn()
  } catch (e) {
    return Promise.reject(e)
  }
}

export const isLoggedIn = async (db: RxDatabase) =>
  typeof (await (db.appuser as RxCollection<AppUser>).findOne().exec())
    ?.rowId === 'string'

export const setupReplication = async (
  db: RxDatabase,
  state: RxState<ApplicationAuthState>,
  networkState: RxState<ApplicationNetworkState>,
  posthog?: PostHog
) => {
  // we proxy our graphql server to the frontend, so we can use relative urls
  const graphqlServerPath = '/graphql'

  const url = {
    ws: graphqlServerPath,
    http: graphqlServerPath,
  }

  const autoStartReplication = await isLoggedIn(db)

  // setup entities to replicate
  const appUserReplication = createAppUserReplication(
    db,
    url,
    autoStartReplication
  )
  const assignmentReplication = createAssignmentReplication(
    db,
    url,
    autoStartReplication
  )
  const assessmentReplication = createAssessmentReplication(
    db,
    url,
    autoStartReplication
  )
  const assessmentDetailReplication = createAssessmentDetailReplication(
    db,
    url,
    autoStartReplication
  )

  const assessmentMeasurementReplication =
    createAssessmentMeasurementReplication(db, url, autoStartReplication)

  const signatureReplication = createSignatureReplication(
    db,
    url,
    autoStartReplication
  )

  const assessmentAdditionalInfoReplication =
    createAssessmentAdditionalInfoReplication(db, url, autoStartReplication)

  const combinedObservable = combineLatest([
    appUserReplication.active$,
    assignmentReplication.active$,
    assessmentReplication.active$,
    assessmentDetailReplication.active$,
    assessmentAdditionalInfoReplication.active$,
    assessmentMeasurementReplication.active$,
    signatureReplication.active$,
  ]).pipe(
    // The combine function emits true if at least one observable is true
    map((values) => {
      return values.some((value) => value)
    }),
    // Filter out consecutive duplicate values
    distinctUntilChanged(),
    share()
  )

  // Captures immediate true values
  const immediateTrue = combinedObservable.pipe(filter((value) => value))

  // Captures debounced values
  const debounced = combinedObservable.pipe(debounceTime(1000))

  // Merge immediateTrue and debounced observables
  const actives = merge(immediateTrue, debounced)

  const activeReplications = actives.subscribe(async (value) => {
    if (!value) {
      await networkState.set('lastError', () => ({ error: null }))
    }
    await networkState.set('status', () =>
      value ? NetworkStatus.SYNCING : NetworkStatus.IDLE
    )
  })

  // collect errors from all replications
  const errors = merge(
    appUserReplication.error$,
    assignmentReplication.error$,
    assessmentReplication.error$,
    assessmentDetailReplication.error$,
    assessmentAdditionalInfoReplication.error$,
    assessmentMeasurementReplication.error$,
    signatureReplication.error$
  )

  const errorsSubscription = errors.subscribe(async (ev) => {
    const error = ev.parameters?.errors?.[0]
    console.error(`Exception during data fetching`, ev)
    posthog?.captureException(ev)
    if (ev instanceof RxError && error) {
      await networkState.set('lastError', () => ({ error }))
    }
    if (error?.message === 'Permission denied') {
      console.warn('Unable to authenticate. Require login')
      await state.set('requireAuthentication', () => 'IS_REQUIRED')
    }
  })

  const authSubscription = state
    .get$('requireAuthentication')
    .subscribe(async (value: AuthState) => {
      if (value === 'IS_NOT_REQUIRED') {
        try {
          await state.set('requireAuthentication', (current: AuthState) => {
            return current === 'IS_NOT_REQUIRED' ? 'PENDING' : current
          })
        } catch (e) {
          console.log('Unexpected error while updating state', e)
          posthog?.captureException(e)
        }
        if (!autoStartReplication) {
          await Promise.all([
            appUserReplication.start(),
            assignmentReplication.start(),
            assessmentReplication.start(),
            assessmentDetailReplication.start(),
            assessmentAdditionalInfoReplication.start(),
            assessmentMeasurementReplication.start(),
            signatureReplication.start(),
          ])
        }
      }
    })

  // This is called on cleanup: Make sure to remove all subscriptions and replications here
  return async () => {
    authSubscription.unsubscribe()
    errorsSubscription.unsubscribe()
    activeReplications.unsubscribe()

    // Some may fail when starting `cancel` (without awaiting), so we wrap them
    return await Promise.allSettled([
      tryCatch(() => appUserReplication.cancel()),
      tryCatch(() => assignmentReplication.cancel()),
      tryCatch(() => assessmentReplication.cancel()),
      tryCatch(() => assessmentDetailReplication.cancel()),
      tryCatch(() => assessmentAdditionalInfoReplication.cancel()),
      tryCatch(() => assessmentMeasurementReplication.cancel()),
      tryCatch(() => signatureReplication.cancel()),
    ]).then((results) => {
      const maybeRejected = results.find(
        (result) => result.status === 'rejected'
      )
      if (maybeRejected) {
        throw new Error('Unable to cancel all replications', {
          cause: maybeRejected.reason,
        })
      }
    })
  }
}
